// ── MenuEngineeringMatrix — 메뉴 4분면 산점도 ──
// Star · Plowhorse · Puzzle · Dog 시각화
// x축: 판매수량(인기), y축: 마진율(수익성), 임계선 중간 분할

import React, { useState, useMemo } from 'react';
import {
  classifyMenuEngineering, groupByQuadrant, quadrantActionMessage, QUADRANT_META,
} from '../utils/menuEngineering';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function MenuEngineeringMatrix({ menus = [], isMobile = false }) {
  const [selectedId, setSelectedId] = useState(null);

  const { classified, threshold, summary, groups } = useMemo(() => {
    const result = classifyMenuEngineering(menus);
    return {
      classified: result.menus,
      threshold: result.threshold,
      summary: result.summary,
      groups: groupByQuadrant(result.menus),
    };
  }, [menus]);

  // 데이터 부족
  if (summary.insufficientData) {
    return (
      <div style={{
        background: '#fff', borderRadius: 14, padding: '20px 22px',
        marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#374151', marginBottom: 6 }}>
          📊 메뉴 엔지니어링 (4분면)
        </div>
        <div style={{
          padding: '20px', textAlign: 'center', background: '#f9fafb',
          borderRadius: 8, color: '#9ca3af', fontSize: 13, lineHeight: 1.6,
        }}>
          {summary.message}
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 8 }}>
            운영 → 매출 입력에서 <strong>메뉴별 입력 모드</strong>로 판매수량을 등록하면 분석됩니다
          </div>
        </div>
      </div>
    );
  }

  // SVG viewBox 좌표계
  const W = isMobile ? 320 : 480;
  const H = isMobile ? 280 : 340;
  const padL = 50, padR = 12, padT = 14, padB = 36;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  // 데이터 범위
  const maxQty = Math.max(...classified.map(m => m.popularity), threshold.popularity * 1.5);
  const minMargin = Math.min(...classified.map(m => m.profitability), 0);
  const maxMargin = Math.max(...classified.map(m => m.profitability), threshold.profitability * 1.3);
  const marginRange = Math.max(1, maxMargin - minMargin);

  const xScale = qty => padL + (qty / maxQty) * plotW;
  const yScale = margin => padT + plotH - ((margin - minMargin) / marginRange) * plotH;

  // 임계선 위치
  const thresholdX = xScale(threshold.popularity);
  const thresholdY = yScale(threshold.profitability);

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: isMobile ? '14px 12px' : '20px 22px',
      marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a1a' }}>
          📊 메뉴 엔지니어링 (4분면)
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, lineHeight: 1.5 }}>
          외식업 컨설팅 표준 분석법 · 인기(판매수량) × 수익성(마진율)
          <br/>임계선: 판매 {threshold.popularity}회, 마진 {threshold.profitability}%
        </div>
      </div>

      {/* 분면별 카운트 칩 */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {Object.entries(summary.counts).map(([q, count]) => (
          <div key={q} style={{
            padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700,
            background: `${QUADRANT_META[q].color}22`,
            color: QUADRANT_META[q].color,
            border: `1px solid ${QUADRANT_META[q].color}55`,
          }}>
            {QUADRANT_META[q].label} {count}개
          </div>
        ))}
      </div>

      {/* SVG 4분면 */}
      <div style={{ overflow: 'auto', marginBottom: 14 }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W, display: 'block', margin: '0 auto' }}>
          {/* 4분면 배경 (옅게) */}
          <rect x={padL} y={padT} width={thresholdX - padL} height={thresholdY - padT}
            fill="#0369a122" />  {/* puzzle 좌상 */}
          <rect x={thresholdX} y={padT} width={W - padR - thresholdX} height={thresholdY - padT}
            fill="#FF6F0F22" />  {/* star 우상 */}
          <rect x={padL} y={thresholdY} width={thresholdX - padL} height={H - padB - thresholdY}
            fill="#9ca3af22" />  {/* dog 좌하 */}
          <rect x={thresholdX} y={thresholdY} width={W - padR - thresholdX} height={H - padB - thresholdY}
            fill="#EA580C22" />  {/* plowhorse 우하 */}

          {/* 4분면 라벨 */}
          <text x={(padL + thresholdX) / 2} y={padT + 16} textAnchor="middle"
            fontSize="10" fontWeight="700" fill="#0369a1">🧩 Puzzle</text>
          <text x={(thresholdX + W - padR) / 2} y={padT + 16} textAnchor="middle"
            fontSize="10" fontWeight="700" fill="#FF6F0F">⭐ Star</text>
          <text x={(padL + thresholdX) / 2} y={H - padB - 6} textAnchor="middle"
            fontSize="10" fontWeight="700" fill="#9ca3af">🐕 Dog</text>
          <text x={(thresholdX + W - padR) / 2} y={H - padB - 6} textAnchor="middle"
            fontSize="10" fontWeight="700" fill="#EA580C">🐴 Plowhorse</text>

          {/* 임계선 (점선) */}
          <line x1={thresholdX} y1={padT} x2={thresholdX} y2={H - padB}
            stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
          <line x1={padL} y1={thresholdY} x2={W - padR} y2={thresholdY}
            stroke="#9ca3af" strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />

          {/* 축 */}
          <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />
          <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#d1d5db" strokeWidth="1" />

          {/* y축 마진 0 라인 (강조) */}
          {minMargin < 0 && (
            <line x1={padL} y1={yScale(0)} x2={W - padR} y2={yScale(0)}
              stroke="#52525B" strokeWidth="1" strokeDasharray="2,4" opacity="0.5" />
          )}

          {/* 데이터 점 */}
          {classified.map(m => {
            const x = xScale(m.popularity);
            const y = yScale(m.profitability);
            const color = QUADRANT_META[m.quadrant]?.color || '#9ca3af';
            const isSelected = selectedId === m.id;
            return (
              <g key={m.id} style={{ cursor: 'pointer' }}
                onClick={() => setSelectedId(isSelected ? null : m.id)}>
                <circle cx={x} cy={y} r={isSelected ? 8 : 5}
                  fill={color} fillOpacity={isSelected ? 0.9 : 0.7}
                  stroke="#fff" strokeWidth="2" />
                {isSelected && (
                  <text x={x} y={y - 12} textAnchor="middle"
                    fontSize="11" fontWeight="700" fill="#1a1a1a"
                    style={{ pointerEvents: 'none' }}>
                    {m.name}
                  </text>
                )}
              </g>
            );
          })}

          {/* 축 라벨 */}
          <text x={W / 2} y={H - 6} textAnchor="middle" fontSize="11" fill="#6b7280">
            인기 (판매수량) →
          </text>
          <text x={padL - 35} y={H / 2} textAnchor="middle" fontSize="11" fill="#6b7280"
            transform={`rotate(-90 ${padL - 35} ${H / 2})`}>
            수익성 (마진율 %) →
          </text>
        </svg>
      </div>

      {/* 선택된 메뉴 액션 추천 */}
      {selectedId && (() => {
        const m = classified.find(x => x.id === selectedId);
        if (!m) return null;
        const meta = QUADRANT_META[m.quadrant];
        return (
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            background: `${meta.color}11`, border: `1px solid ${meta.color}44`,
            marginBottom: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: meta.color }}>
              {meta.label} · {m.name}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3 }}>
              판매 {m.popularity}회 · 마진 {m.profitability}% · 매출 {fmt(m.actualSales?.revenue || 0)}원
            </div>
            <div style={{ fontSize: 12, color: '#374151', marginTop: 6, lineHeight: 1.5 }}>
              {quadrantActionMessage(m.quadrant, m)}
            </div>
          </div>
        );
      })()}
      {!selectedId && (
        <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', padding: '4px 0' }}>
          💡 점을 클릭하면 메뉴별 액션 추천을 볼 수 있어요
        </div>
      )}

      {/* 분면별 메뉴 리스트 */}
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
        {['star', 'plowhorse', 'puzzle', 'dog'].map(q => {
          const meta = QUADRANT_META[q];
          const list = groups[q];
          if (list.length === 0) return null;
          return (
            <div key={q} style={{
              padding: '10px 12px', borderRadius: 8,
              background: `${meta.color}11`, border: `1px solid ${meta.color}44`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: meta.color, marginBottom: 5 }}>
                {meta.label} {list.length}개
              </div>
              <div style={{ fontSize: 11, color: '#374151', lineHeight: 1.7 }}>
                {list.slice(0, 5).map(m => m.name).join(', ')}
                {list.length > 5 && ` 외 ${list.length - 5}개`}
              </div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4 }}>
                {meta.desc}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
